import { ButtonHTMLAttributes, Ref } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Chip } from '@flipster/react/design-system/chip';
import CalendarDaysIcon from '@flipster/react/icon/generated/CalendarDaysIcon';

import { DatePicker } from '../../../shared/date-picker';
import type { DateRange } from '../../../shared/date-picker/date-picker';

interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'className' | 'style'> {
  selected: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * 비선택 상태의 아이콘만 밝게 쓴다. 선택되면 칩 배경이 흰색(`inverse.white`)으로 바뀌므로
 * 디자인 시스템 recipe의 `inverse.black`을 그대로 둬야 아이콘이 보인다.
 *
 * Trigger가 cloneElement로 ref와 이벤트 핸들러를 넘기므로 나머지 props는 그대로 전달한다.
 */
const DateRangeChip = ({ selected, ...restProps }: ChipProps) => (
  <Chip.Choice
    type='square'
    icon={<CalendarDaysIcon />}
    selected={selected}
    css={selected ? undefined : { color: 'content.default.level-5' }}
    {...restProps}
  />
);

interface Props {
  selected?: DateRange;
  onChange: (range?: { startDate: Date; endDate: Date }) => void;
  /** 일 대신 월 단위로 고르게 한다 */
  isMonthly?: boolean;
  /** 날짜를 UTC 기준으로 다룬다 */
  isUtc?: boolean;
  maxRangeDays?: number;
  onOpen?: () => void;
}

/** 히스토리 화면의 기간 필터. 달력 칩 트리거 + 기간 선택 팝오버를 함께 제공한다 */
export const DateRangeFilter = ({ selected, onChange, isMonthly, isUtc, maxRangeDays, onOpen }: Props) => (
  <DatePicker.Root onOpen={onOpen}>
    <DatePicker.Trigger asChild>
      <DateRangeChip selected={Boolean(selected)} />
    </DatePicker.Trigger>
    <DatePicker.RangePicker
      portal
      selected={selected}
      onChange={onChange}
      confirmText={<FormattedMessage id='Common.r250923.Search' />}
      isMonthly={isMonthly}
      isUtc={isUtc}
      maxRangeDays={maxRangeDays}
    />
  </DatePicker.Root>
);
